import { useApolloClient } from '@apollo/react-hooks';
import styled from '@emotion/styled';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toastNotificationsService } from 'src/config/rootService';
import { AnswerOption } from 'src/generated/webapp_gql';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import { Page, PageProps } from 'src/modules/newPrograms/components/Page';
import { useProgramContext } from 'src/modules/newPrograms/contexts/programContext';
import { ProgramPageFragment } from 'src/modules/newPrograms/documents/getProgram.generated';
import { DeprecatedTextArea } from 'src/shared/components/Input';
import { color } from 'src/shared/styles/variables';
import { selectAnswerOption } from '../effects';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import { Button, pxToRem } from '@modernhealth/vital-components';

const AnswersContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginTop: 30,
    width: 500,
    alignSelf: 'center',
});

const TextAreaContainer = styled.div({
    marginTop: 10,
    textarea: {
        border: `2px solid ${color.linkWaterLight}`,
    },
});

const SelectButton = styled(Button)<{ isSelected: boolean }>(({ isSelected }) => ({
    '&&': {
        borderColor: isSelected ? color.royalBlue : color.linkWaterLight,
        fontWeight: isSelected ? 700 : 400,
        lineHeight: 1.25,
        padding: '0.88rem 0.88rem 0.68rem',
    },
    '&:focus-visible': {
        boxShadow: `0px 0px 0px 2px ${dsColors.cyan20}`,
    },
}));

interface SelectPageProps extends PageProps {
    page: ProgramPageFragment;
}

export const SelectPage: FC<SelectPageProps> = ({
    page,
    onNavigatePrev,
    onNavigateNext,
    navigationComponent,
}) => {
    const { t } = useTranslation();
    const { canSelectNextAnswer, canContinue } = useProgramContext();
    const client = useApolloClient();

    const handleSelectAnswer = (answer: AnswerOption, index: number) => {
        if (!answer.isSelected && !canSelectNextAnswer) {
            return toastNotificationsService.renderWarningToast(
                t('programs:program.multiselect_page.max_selected'),
            );
        }

        selectAnswerOption(client, {
            pageId: page.id,
            answerIndex: index,
        });
    };

    const handleNext = () => {
        if (canContinue) {
            onNavigateNext();
        } else {
            return toastNotificationsService.renderWarningToast(
                t('programs:program.multiselect_page.min_not_selected', {
                    count: page.parsedMultiselectConfig?.minSelectable ?? 1,
                }),
            );
        }
    };

    return (
        <Page
            contentVerticalAlignment={page.contentVerticalAlignment}
            onNavigatePrev={onNavigatePrev}
            onNavigateNext={handleNext}
            navigationComponent={navigationComponent}
            justifyNavigation="center"
        >
            <Markdown
                textHorizontalAlignment={page.contentHorizontalAlignment}
                text={page.content ?? ''}
            />
            <AnswersContainer>
                {page.parsedAnswerOptions?.map((answer, i) => {
                    return (
                        <React.Fragment
                            key={`answer-${i}-${answer.text ?? 'unknown'}-${
                                answer.customOptions ?? 'options'
                            }`}
                        >
                            <SelectButton
                                onClick={() => handleSelectAnswer(answer, i)}
                                variant="secondary"
                                style={{
                                    marginTop: pxToRem(10),
                                    whiteSpace: 'normal',
                                }}
                                isSelected={!!answer.isSelected}
                            >
                                {answer.text}
                            </SelectButton>
                            {answer.customOptions && answer.isSelected && (
                                <TextAreaContainer>
                                    <DeprecatedTextArea
                                        value={answer.customOptionValue ?? ''}
                                        onChange={data => {
                                            selectAnswerOption(client, {
                                                pageId: page.id,
                                                answerIndex: i,
                                                customOptionValue: String(data),
                                                isSelected: true,
                                            });
                                        }}
                                        placeholder={answer.customOptions?.placeholder ?? undefined}
                                        fieldId="customAnswer"
                                    />
                                </TextAreaContainer>
                            )}
                        </React.Fragment>
                    );
                })}
            </AnswersContainer>
        </Page>
    );
};
